import { useState, useCallback } from "react";
import { FileText, RefreshCw } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { useNotificationLogMonitoring } from "../hooks/useNotificationLogMonitoring";
import type { NotificationLogStatus } from "../types";
import {
  NOTIFICATION_LOG_STATUSES,
  LOG_STATUS_COLOR,
  LOG_STAT_CARD_COLOR,
  NOTIFICATION_TYPES,
  NOTIFICATION_TYPE_LABEL_KEYS,
} from "../constants";

// ─── Stats Bar ─────────────────────────────────────────────────────────────

function NotificationLogStatsBar({
  stats,
  loading,
}: {
  stats?: {
    total: number;
    queuedCount: number;
    sentCount: number;
    deliveredCount: number;
    failedCount: number;
    droppedCount: number;
  };
  loading: boolean;
}) {
  const { t } = useLocale();

  const cards = [
    { labelKey: "notificationLogs.stats.total", value: stats?.total ?? 0, color: LOG_STAT_CARD_COLOR.total },
    { labelKey: "notificationLogs.stats.queued", value: stats?.queuedCount ?? 0, color: LOG_STAT_CARD_COLOR.queued },
    { labelKey: "notificationLogs.stats.sent", value: stats?.sentCount ?? 0, color: LOG_STAT_CARD_COLOR.sent },
    { labelKey: "notificationLogs.stats.delivered", value: stats?.deliveredCount ?? 0, color: LOG_STAT_CARD_COLOR.delivered },
    { labelKey: "notificationLogs.stats.failed", value: stats?.failedCount ?? 0, color: LOG_STAT_CARD_COLOR.failed },
    { labelKey: "notificationLogs.stats.dropped", value: stats?.droppedCount ?? 0, color: LOG_STAT_CARD_COLOR.dropped },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((card) => (
        <div key={card.labelKey} className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs font-medium text-muted-foreground">{t(card.labelKey)}</p>
          <p className={`mt-1 text-2xl font-bold ${card.color}`}>
            {loading ? "…" : card.value.toLocaleString()}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─── Table ──────────────────────────────────────────────────────────────────

function NotificationLogTable({
  entries,
  loading,
}: {
  entries: {
    userId: string;
    type: string;
    title: string;
    body: string;
    status: NotificationLogStatus;
    failureReason?: string;
    sentAt: string | null;
    createdAt: string;
  }[];
  loading: boolean;
}) {
  const { t } = useLocale();

  function formatTime(dateStr: string | null): string {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  }

  function truncate(text: string, max: number): string {
    return text.length > max ? text.slice(0, max) + "…" : text;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-border bg-muted/50 text-xs font-medium uppercase text-muted-foreground">
          <tr>
            <th className="px-4 py-3">{t("notificationLogs.table.userId")}</th>
            <th className="px-4 py-3">{t("notificationLogs.table.type")}</th>
            <th className="px-4 py-3">{t("notificationLogs.table.title")}</th>
            <th className="px-4 py-3">{t("notificationLogs.table.status")}</th>
            <th className="px-4 py-3">{t("notificationLogs.table.failureReason")}</th>
            <th className="px-4 py-3">{t("notificationLogs.table.sentAt")}</th>
            <th className="px-4 py-3">{t("notificationLogs.table.createdAt")}</th>
          </tr>
        </thead>
        <tbody>
          {loading && entries.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                {t("common.loading")}
              </td>
            </tr>
          ) : entries.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                {t("notificationLogs.noEntries")}
              </td>
            </tr>
          ) : (
            entries.map((entry, idx) => (
              <tr
                key={`${entry.userId}-${entry.createdAt}-${idx}`}
                className="border-b border-border last:border-0 hover:bg-muted/30"
              >
                <td className="px-4 py-3 font-mono text-xs select-all">
                  {entry.userId}
                </td>
                <td className="px-4 py-3 text-xs">{t(NOTIFICATION_TYPE_LABEL_KEYS[entry.type] ?? entry.type)}</td>
                <td className="px-4 py-3 text-xs" title={entry.title}>
                  {truncate(entry.title, 40)}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${LOG_STATUS_COLOR[entry.status]}`}
                  >
                    {t(`notificationLogs.status.${entry.status}`)}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground" title={entry.failureReason ?? ""}>
                  {entry.failureReason ? truncate(entry.failureReason, 30) : "—"}
                </td>
                <td className="px-4 py-3 text-xs">{formatTime(entry.sentAt)}</td>
                <td className="px-4 py-3 text-xs">{formatTime(entry.createdAt)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

export function NotificationLogMonitoringPage() {
  const { t } = useLocale();

  const [statusFilter, setStatusFilter] = useState<NotificationLogStatus | "ALL">("ALL");
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [userIdSearch, setUserIdSearch] = useState("");
  const [appliedUserId, setAppliedUserId] = useState<string>("");
  const [page, setPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const { data, loading, refetch } = useNotificationLogMonitoring({
    status: statusFilter !== "ALL" ? statusFilter : undefined,
    type: typeFilter !== "ALL" ? typeFilter : undefined,
    userId: appliedUserId || undefined,
    first: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });

  const payload = data?.adminNotificationLogMonitoring;
  const entries = payload?.entries ?? [];
  const stats = payload?.stats;
  const totalCount = payload?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const isRefreshing = loading || refreshing;

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setTimeout(() => setRefreshing(false), 600);
    }
  }, [refetch]);

  const handleUserIdSearch = useCallback(() => {
    setAppliedUserId(userIdSearch.trim());
    setPage(0);
  }, [userIdSearch]);

  const handleUserIdKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleUserIdSearch();
    },
    [handleUserIdSearch],
  );

  const handleClearUserId = useCallback(() => {
    setUserIdSearch("");
    setAppliedUserId("");
    setPage(0);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <FileText className="h-7 w-7" />
            {t("notificationLogs.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("notificationLogs.subtitle")}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
          {isRefreshing ? t("dashboard.refreshing") : t("common.refresh")}
        </button>
      </div>

      {/* Stats */}
      <NotificationLogStatsBar stats={stats} loading={loading && !stats} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as NotificationLogStatus | "ALL");
            setPage(0);
          }}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm"
        >
          <option value="ALL">{t("notificationLogs.filter.allStatuses")}</option>
          {NOTIFICATION_LOG_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`notificationLogs.status.${s}`)}
            </option>
          ))}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(0);
          }}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm"
        >
          <option value="ALL">{t("notificationLogs.filter.allTypes")}</option>
          {NOTIFICATION_TYPES.map((nt) => (
            <option key={nt} value={nt}>
              {t(NOTIFICATION_TYPE_LABEL_KEYS[nt] ?? nt)}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-1">
          <input
            type="text"
            value={userIdSearch}
            onChange={(e) => setUserIdSearch(e.target.value)}
            onKeyDown={handleUserIdKeyDown}
            placeholder={t("notificationLogs.filter.searchUserId")}
            className="h-9 w-56 rounded-md border border-border bg-background px-3 text-sm placeholder:text-muted-foreground"
          />
          <button
            onClick={handleUserIdSearch}
            className="h-9 rounded-md border border-border bg-background px-3 text-sm font-medium hover:bg-muted"
          >
            {t("common.search")}
          </button>
          {appliedUserId && (
            <button
              onClick={handleClearUserId}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm text-muted-foreground hover:bg-muted"
            >
              ✕
            </button>
          )}
        </div>

        <span className="ml-auto text-sm text-muted-foreground">
          {totalCount.toLocaleString()} {t("notificationLogs.entryCount")}
        </span>
      </div>

      {/* Table */}
      <NotificationLogTable entries={entries} loading={loading} />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {t("common.showingRange", {
              start: String(page * PAGE_SIZE + 1),
              end: String(Math.min((page + 1) * PAGE_SIZE, totalCount)),
              total: String(totalCount),
            })}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-md border border-border px-3 py-1 text-sm disabled:opacity-50"
            >
              {t("common.previousPage")}
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-md border border-border px-3 py-1 text-sm disabled:opacity-50"
            >
              {t("common.nextPage")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
